import { landmineNames } from '@mm/constants/landmine-names';
import { getPlanInfo } from '@mm/state/helpers/billing-info-helpers';
import { EnhancedBillingPage } from '@mm/state/interfaces/connected-page';
import { EnhancedPlan } from '@mm/state/interfaces/subscription';
import { EnhancedOutgoingMessagesStatistic } from '@mm/state/model/ConnectedPageModel';
import modalActions from '@mm/state/modules/modalsState/actions';
import {
  activeFacebookPageSelector,
  activeMessagesStatisticSelector,
} from '@mm/state/selectors/facebookPageSelector';
import {
  defaultProPlanSelector,
  defaultProUnicornPlanSelector,
} from '@mm/state/selectors/subscriptionSelectors';
import { activeCompanySelector } from '@mm/state/selectors/viewSelectors';
import { getIsAutomationFeatureEnabled } from '@mm/state/selectors/automations';
import { StoreState } from '@mm/state/store-interfaces';
import { combinedFeatureFlagsSelector } from '@mm/state/selectors/featureFlagsSelectors';
import { CloseOutlined } from '@ant-design/icons';
import classNames from 'classnames';
import React, { useCallback, useState } from 'react';
import { connect, useSelector } from 'react-redux';
import { compose } from 'recompose';
import { Dispatch } from 'redux';
import useRouter from 'use-react-router';
import {
  ButtonColor,
  PrimaryButton,
} from '../../../../common/buttons/PrimaryButton/PrimaryButton';
import { ReactComponent as WarningIcon } from './warning.svg';
import rocketIcon from './rocket.svg';
import { ConnectedPopover } from '../../../../common/components/ConnectedPopover/ConnectedPopover';
import { useFreeTrialUpgradeModal } from '../../../../common/modals/FreeTrialUpgradeModal/use-free-trial-upgrade-modal';
import { getBucketedPercentage } from '../../../../common/modals/templates/SendLimitNoticeTemplate/SendLimitNoticeTemplate';
import { useLandmineOpensHelpers } from '../../../../common/modals/UniversalUpgradeModal/useLandmineOpensHelpers';
import OveragesManagement from '../../../../dashboard/pages/SettingsPage/BillingPanel/SubscriptionTabsArea/ActiveSubscriptionTab/OveragesManagement/OveragesManagement';
import { getUtilizationWarningText } from './helpers';
import './SendLimitAlert.pcss';
import { PercentageBar } from '../../../../common/components/PercentageBar/PercentageBar';

type OuterProps = {};

type InnerProps = ReturnType<typeof mapStateToProps> &
  ReturnType<typeof mapDispatchToProps>;

type Props = InnerProps & OuterProps;

const getMeetsPercentageThreshold = (
  messagesStatistic: EnhancedOutgoingMessagesStatistic,
  facebookPage?: EnhancedBillingPage,
) => {
  const planInfo = getPlanInfo(facebookPage);

  if (facebookPage) {
    if (planInfo.isFreePlan) {
      return facebookPage.messagesStatistic.utilizationPercentage > 79;
    }

    return messagesStatistic.utilizationPercentage > 89;
  }

  return false;
};

const SendLimitAlert = (props: Props) => {
  const {
    messagesStatistic,
    openBillingModal,
    defaultProPlan,
    defaultUnicornPlan,
    facebookPage,
    company,
  } = props;

  const [messageVisible, setMessageVisible] = useState(true);
  const meetsPercentageThreshold = getMeetsPercentageThreshold(
    messagesStatistic,
    facebookPage,
  );
  const overagesEnabled = facebookPage && !facebookPage.overagesEnabled;
  const ownerOrAdmin = company && company.editable;
  const { november2021Pricing = false } = useSelector(
    combinedFeatureFlagsSelector,
  );

  const { isFreeTrialPromotionWithFutureSubscription } =
    useFreeTrialUpgradeModal(facebookPage);

  const onIconClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();
      setMessageVisible(!messageVisible);
    },
    [messageVisible, setMessageVisible],
  );

  const onUpgradeClick = () => {
    if (november2021Pricing && defaultUnicornPlan && facebookPage) {
      openBillingModal(facebookPage, defaultUnicornPlan);
    } else if (defaultProPlan && facebookPage) {
      openBillingModal(facebookPage, defaultProPlan);
    }

    setMessageVisible(false);
  };
  const handleContainerClick = (e: React.MouseEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const containerClassName = classNames('send-limit-alert__popover-wrapper');

  if (
    (!isFreeTrialPromotionWithFutureSubscription &&
      overagesEnabled &&
      meetsPercentageThreshold &&
      ownerOrAdmin &&
      facebookPage) ||
    (facebookPage && facebookPage.hardLimitReached) ||
    (facebookPage &&
      !facebookPage.twilioCampaignCreated &&
      facebookPage.twilioDailyLocked)
  ) {
    return (
      <div className="send-limit-alert" onClick={handleContainerClick}>
        <ConnectedPopover
          isOpen={messageVisible}
          content={() => (
            <SendLimitAlertMessage
              facebookPage={facebookPage}
              setMessageVisible={setMessageVisible}
              onUpgradeClick={onUpgradeClick}
              messagesStatistic={messagesStatistic}
              ownerOrAdmin={!!ownerOrAdmin}
              defaultProPlan={defaultProPlan}
              defaultUnicornPlan={defaultUnicornPlan}
            />
          )}
          containerClassName={containerClassName}
          position="bottom"
          align="center"
          padding={37}
        >
          <div className="send-limit-alert__icon" onClick={onIconClick}>
            <WarningIcon />
          </div>
        </ConnectedPopover>
      </div>
    );
  }

  return null;
};

type SendLimitAlertMessageProps = {
  facebookPage: EnhancedBillingPage;
  setMessageVisible: (messageVisible: boolean) => void;
  messagesStatistic: EnhancedOutgoingMessagesStatistic;
  onUpgradeClick: () => void;
  ownerOrAdmin: boolean;
  defaultProPlan: EnhancedPlan | undefined;
  defaultUnicornPlan: EnhancedPlan | undefined;
};

const SendLimitAlertMessage = (props: SendLimitAlertMessageProps) => {
  const { history } = useRouter();
  const {
    setMessageVisible,
    messagesStatistic,
    onUpgradeClick,
    facebookPage,
    ownerOrAdmin,
    defaultProPlan,
    defaultUnicornPlan,
  } = props;

  const { november2021Pricing = false } = useSelector(
    combinedFeatureFlagsSelector,
  );
  const isAutomationsEnabled = useSelector(getIsAutomationFeatureEnabled);
  const text = getUtilizationWarningText(
    facebookPage,
    messagesStatistic.utilizationPercentage,
    november2021Pricing,
    isAutomationsEnabled,
  );
  const messageText =
    messagesStatistic.cappedCount === 1 ? 'send credit' : 'send credits';
  const planInfo = getPlanInfo(facebookPage);
  const getTextForATooltip = () => {
    if (facebookPage.hardLimitReached) {
      if (planInfo.isCustomTeam || planInfo.isLegacyTeamPlan) {
        return `Purchase Send Credits`;
      }
      return `Upgrade`;
    }
  };

  const handleClick = () => {
    if (facebookPage.hardLimitReached) {
      history.push('/settings/billing');
      return;
    }
    window.open(
      'https://mobilemonkey.com/help/article/03nc6a7axs-send-overage-automatic-upgrades',
    );
  };

  const ownership = ownerOrAdmin ? 'OWNER_OR_ADMIN' : 'NOT_OWNER_OR_ADMIN';

  const bucketedPercentage = getBucketedPercentage(
    messagesStatistic.utilizationPercentage,
  );
  const landmineName = `${landmineNames.SENDS_USAGE__TOOLTIP}__${ownership}__${bucketedPercentage}`;
  const { landmineOpenId, landmineOpensCRUDActions } = useLandmineOpensHelpers(
    landmineName,
    november2021Pricing
      ? defaultUnicornPlan?.name
      : defaultProPlan?.name || 'unknown Pro plan',
  );

  return (
    <div className="send-limit-alert-message">
      <img src={rocketIcon} />
      <div className="send-limit-alert-message-wrapper">
        {!facebookPage.twilioDailyLocked && (
          <div className="send-limit-alert-message__top-section">
            Upgrade your plan
          </div>
        )}
        {typeof text === 'string' && (
          <div className="send-limit-alert-message__bottom-section">{text}</div>
        )}

        {typeof text !== 'string' && (
          <>
            <div className="send-limit-alert-message__row">{text?.row1}</div>
            <div className="send-limit-alert-message__row">{text?.row2}</div>
            {facebookPage.messagesStatistic.utilizationPercentage > 79 && (
              <>
                <PercentageBar
                  cardLayout={true}
                  showTotalNumber
                  total={messagesStatistic.outgoingMessageLimit}
                  used={
                    messagesStatistic.pageSpecificCount ||
                    messagesStatistic.count
                  }
                  showUsedNumber={false}
                  className="send-limit-alert-message__usage-bar"
                />

                <div className="send-limit-alert-message__row">{text.row3}</div>
              </>
            )}
          </>
        )}

        <div className="send-limit-alert-message__buttons-wrapper">
          {facebookPage.twilioDailyLocked &&
            !facebookPage.twilioCampaignCreated && (
              <PrimaryButton
                className="send-limit-alert-message__button"
                onClick={() =>
                  window.open(
                    'mailto:support@mobilemonkey.com?subject=SMS Campaign Registration Request',
                  )
                }
                color={ButtonColor.green}
              >
                Contact Suport
              </PrimaryButton>
            )}

          {planInfo.isFreePlan && !facebookPage.twilioDailyLocked && (
            <PrimaryButton
              className="send-limit-alert-message__button"
              onClick={() => {
                if (landmineOpenId) {
                  landmineOpensCRUDActions.update(landmineOpenId, true);
                }
                onUpgradeClick();
              }}
              color={ButtonColor.blue}
            >
              Upgrade to {november2021Pricing ? 'Startup' : 'Operator'}
            </PrimaryButton>
          )}

          {!planInfo.isFreePlan && !facebookPage.twilioDailyLocked && (
            <OveragesManagement
              facebookPage={facebookPage}
              subscription={facebookPage.subscription}
              textOverride={getTextForATooltip()}
              showLink
              hideQuestionIcon
              navigationPath="/settings/billing"
              className="send-limit-alert-message__overage-management"
              buttonColor={ButtonColor.blue}
            >
              <a
                onClick={handleClick}
                className="send-limit-alert-message__overage-management-link"
                href="https://mobilemonkey.com/help/article/03nc6a7axs-send-overage-automatic-upgrades"
                target="_blank"
                rel="noopener noreferrer"
              >
                View Pricing
              </a>
            </OveragesManagement>
          )}
          <PrimaryButton
            className="send-limit-alert-message__button-black"
            onClick={() => setMessageVisible(false)}
            color="transparent-no-shadow"
          >
            Not now
          </PrimaryButton>
        </div>
        <CloseOutlined
          className="send-limit-alert-message__close-button"
          onClick={() => setMessageVisible(false)}
        />
      </div>
    </div>
  );
};

const mapStateToProps = (state: StoreState) => ({
  defaultProPlan: defaultProPlanSelector(state),
  defaultUnicornPlan: defaultProUnicornPlanSelector(state),
  company: activeCompanySelector(state),
  facebookPage: activeFacebookPageSelector(state),
  messagesStatistic: activeMessagesStatisticSelector(state),
});

const mapDispatchToProps = (dispatch: Dispatch<StoreState>) => ({
  openBillingModal: (facebookPage: EnhancedBillingPage, plan: EnhancedPlan) =>
    dispatch(
      modalActions.openBillingModal({
        facebookPage,
        plan,
        subscription: facebookPage.subscription,
      }),
    ),
});

const enhance = compose<InnerProps, OuterProps>(
  connect(mapStateToProps, mapDispatchToProps),
);

export default enhance(SendLimitAlert);
